import { motion } from 'framer-motion';
import ContactForm from '../ContactForm/ContactForm';
import SectionHeader from '../ui/SectionHeader';
import MotionReveal from '../ui/MotionReveal';
import {
  availability,
  contactMethods,
  personalInfo,
  sectionCopy,
  services,
} from '../../data/portfolioData';
import { cardHover, ease } from '../../motion/variants';

const ContactIcon = ({ type }) => {
  const icons = {
    Email: (
      <svg className="w-5 h-5 text-ant-gold" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
      </svg>
    ),
    LinkedIn: (
      <svg className="w-5 h-5 text-ant-gold" fill="currentColor" viewBox="0 0 24 24">
        <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
      </svg>
    ),
    GitHub: (
      <svg className="w-5 h-5 text-ant-gold" fill="currentColor" viewBox="0 0 24 24">
        <path fillRule="evenodd" d="M12 2C6.48 2 2 6.48 2 12c0 4.42 2.865 8.167 6.84 9.49.5.094.68-.217.68-.48 0-.237-.008-.866-.013-1.7-2.782.604-3.365-1.34-3.365-1.34-.454-1.155-1.11-1.464-1.11-1.464-.908-.62.07-.608.07-.608 1.004.07 1.53 1.03 1.53 1.03.89 1.525 2.34 1.085 2.91.83.093-.645.35-1.086.635-1.336-2.22-.25-4.555-1.11-4.555-4.937 0-1.09.39-1.98 1.03-2.68-.103-.25-.448-1.27.097-2.647 0 0 .84-.27 2.75 1.024A9.57 9.57 0 0112 6.763c.855.004 1.715.116 2.517.345 1.91-1.295 2.75-1.024 2.75-1.024.546 1.377.2 2.397.1 2.647.64.7 1.03 1.59 1.03 2.68 0 3.837-2.34 4.687-4.57 4.937.36.31.68.918.68 1.848 0 1.335-.012 2.41-.012 2.735 0 .265.18.575.688.48C19.135 20.167 22 16.42 22 12c0-5.52-4.48-10-10-10z" clipRule="evenodd" />
      </svg>
    ),
  };
  return icons[type] || null;
};

const ContactSection = () => {
  const copy = sectionCopy.contact;

  return (
    <section id="contact" className="section-padding bg-ant-bg text-ant-fg border-t border-ant-line-soft">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionHeader
          label={copy.label}
          titleLine1={copy.titleLine1}
          titleLine2={copy.titleLine2}
          subtitle={`${copy.subtitle} — ${personalInfo.location.split(',')[0]} / Remote.`}
          align="center"
          className="text-center mx-auto"
        />

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-5 mb-12 sm:mb-16 max-w-5xl mx-auto">
          {contactMethods.map((method, i) => (
            <MotionReveal key={method.name} delay={i * 80} variant="scale">
              <motion.a
                href={method.link}
                target={method.link.startsWith('http') ? '_blank' : '_self'}
                rel={method.link.startsWith('http') ? 'noopener noreferrer' : ''}
                initial="rest"
                whileHover="hover"
                variants={cardHover}
                transition={{ duration: 0.35, ease }}
                className="ant-card ant-ring ant-card-hover block p-5 sm:p-6 rounded-2xl border border-white/[0.08] bg-white/[0.02] hover:border-white/[0.14] text-center group h-full"
              >
                <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-xl bg-ant-gold/10 border border-ant-gold/20 flex items-center justify-center mx-auto mb-3 sm:mb-4">
                  <ContactIcon type={method.name} />
                </div>
                <h3 className="text-base sm:text-lg font-semibold text-white mb-1">{method.name}</h3>
                <p className="text-xs sm:text-sm text-white/40 mb-2">{method.description}</p>
                <p className="text-xs sm:text-sm font-medium text-ant-gold/80 break-all">{method.value}</p>
              </motion.a>
            </MotionReveal>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mb-12 sm:mb-16">
          <MotionReveal>
            <div className="ant-card ant-ring rounded-2xl border border-white/[0.08] bg-white/[0.02] p-5 sm:p-8 h-full">
              <h3 className="text-lg sm:text-xl font-semibold text-white mb-5 sm:mb-6">{copy.availabilityTitle}</h3>
              <div className="space-y-3">
                {availability.map((item) => (
                  <div
                    key={item.day}
                    className="flex items-center justify-between gap-3 p-3 sm:p-4 rounded-xl border border-white/[0.06] bg-ant-bg-2"
                  >
                    <div className="min-w-0">
                      <p className="font-medium text-white/80 text-sm sm:text-base">{item.day}</p>
                      <p className="text-xs sm:text-sm text-white/35">{item.time}</p>
                    </div>
                    <span className={`font-mono text-[10px] sm:text-xs shrink-0 ${item.color}`}>
                      {item.status}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </MotionReveal>

          <MotionReveal delay={100}>
            <div className="ant-card ant-ring rounded-2xl border border-white/[0.08] bg-white/[0.02] p-5 sm:p-8 h-full">
              <h3 className="text-lg sm:text-xl font-semibold text-white mb-5 sm:mb-6">{copy.servicesTitle}</h3>
              <div className="space-y-3">
                {services.map((service) => (
                  <div
                    key={service.name}
                    className="flex items-start gap-3 p-3 sm:p-4 rounded-xl border border-white/[0.06] bg-ant-bg-2"
                  >
                    <span className="font-mono text-[10px] text-ant-gold/60 mt-1 shrink-0">›</span>
                    <div className="min-w-0">
                      <h4 className="font-semibold text-white/80 text-sm sm:text-base">{service.name}</h4>
                      <p className="text-xs sm:text-sm text-white/40 mb-2">{service.description}</p>
                      <span className="inline-block px-2.5 py-0.5 bg-ant-gold/10 text-ant-gold border border-ant-gold/20 text-[10px] rounded-full font-mono">
                        {service.timeframe}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </MotionReveal>
        </div>

        <MotionReveal>
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-8 sm:mb-10">
              <p className="font-mono text-[11px] tracking-[0.25em] uppercase text-white/35 mb-5">
                {copy.formLabel}
              </p>
              <h3 className="text-xl sm:text-2xl font-semibold text-white">{copy.formTitle}</h3>
            </div>
            <ContactForm />
          </div>
        </MotionReveal>
      </div>
    </section>
  );
};

export default ContactSection;